import { useCallback, useState, useEffect, useRef } from "react";
import { createContainer } from "unstated-next";

import "./style.scss";

interface ToastParams {
    message: string;
    duration: number;
    // Monotonic id so each toast is a fresh DOM node (see key= below) — lets the
    // live region re-announce even when the same message fires twice (4.1.3).
    seq: number;
}

export const ToastContainer = createContainer(function () {
    const [toastParams, setToastParams] = useState<ToastParams | undefined>();
    const seqRef = useRef(0);
    const pendingRef = useRef<number>();

    // Stable identity (useCallback): consumers hold showToast in useCallback/
    // useEffect dependency lists (e.g. agents.view's reload), so a fresh
    // function per render would re-fire those effects on every toast — the
    // agents page silently refetched itself each time a toast appeared.
    const showToast = useCallback((message: string, duration = 2000) => {
        window.clearTimeout(pendingRef.current);
        seqRef.current += 1;
        const seq = seqRef.current;

        // A toast often fires in the same render a modal closes. MUI Modal marks
        // the whole app root `aria-hidden` while open and only restores it in a
        // post-close effect, so a message inserted before that runs lands inside
        // an aria-hidden subtree and NO screen reader announces it (4.1.3). Wait
        // until the live region is no longer inside an aria-hidden ancestor, then
        // insert — deterministic regardless of the modal's teardown timing. With
        // no modal open this resolves immediately (no delay). Capped so a toast
        // fired while a modal deliberately stays open still shows visually.
        const insert = () => setToastParams({ message, duration, seq });
        let waited = 0;
        const tick = () => {
            const region = document.querySelector(".toasts-container");
            const hidden = region?.closest('[aria-hidden="true"]');
            if (!hidden || waited >= 1500) {
                insert();
                return;
            }
            waited += 100;
            pendingRef.current = window.setTimeout(tick, 100);
        };
        tick();
    }, []);

    return {
        toastParams,
        setToastParams,
        showToast
    };
});

export const ToastProvider = ToastContainer.Provider;

const Toast = () => {
    const { toastParams, setToastParams } = ToastContainer.useContainer();
    const [classes, setClasses] = useState("");
    const messageTimeout = useRef<number>();

    useEffect(() => {
        if (toastParams) {
            setClasses("is-visible");
            messageTimeout.current = window.setTimeout(() => {
                setClasses("is-hidden");
                setTimeout(() => {
                    setToastParams(undefined);
                }, 300);
            }, toastParams.duration);
        }

        return () => {
            window.clearTimeout(messageTimeout.current);
        };
    }, [toastParams, setToastParams]);

    return (
        // Persistent live region (WCAG 4.1.3): the container is always in the DOM
        // so a screen reader announces each toast as its text is inserted, without
        // stealing focus. Assertive (role=alert) rather than polite: toasts often
        // fire in the same tick a modal closes / focus moves, and VoiceOver drops a
        // *polite* region when it's mid-announcing that focus change — assertive is
        // spoken reliably. key={seq} remounts the node per toast so repeats re-fire.
        <div className="toasts-container" role="alert" aria-live="assertive" aria-atomic="true">
            {toastParams?.message && (
                <div key={toastParams.seq} className={`toast ${classes}`}>
                    {toastParams.message}
                </div>
            )}
        </div>
    );
};

export default Toast;
